import {
	Box3,
	Vector3,
	Quaternion,
	Object3D,
} from 'three';
import {HOLLOW_SUBTRACTION, SUBTRACTION, Brush, Evaluator} from 'three-bvh-csg';

import {OlivioLuminaire} from './olivioLuminaire';
import {selectOlivioOffsetFromTop} from '../olivioSelectors';

const conf = {
	...OlivioLuminaire.conf,
};

const topMargin = 0.65;

// at 0.140
const minWoodHorizontalOffset = {
	piccolo: 0.028,
	'medio-grande': 0.01,
};
const minWoodRadius = 0.140 / 2;

const getWoodHorizontalMarginByDiameter = (size, diameter) => {
	// we keep the same distance from the edges, that is having the bracket bottom border "touching"
	// the edge, as it appears in the rendering on the website:
	// https://www.selux.com/ger/en/products/olivio/olivio-wood
	const radius = diameter / 2;
	const diff = radius - minWoodRadius + minWoodHorizontalOffset[size];
	return diff;
};


const poleSizes = {
	wood__conical__5: {
		height: 5,
		topMargin: topMargin,
		topDiameter: 0.120,
		bottomDiameter: 0.168,
		baseHeight: 1.2,
	},
	wood__conical__6: {
		height: 6,
		topMargin: topMargin,
		topDiameter: 0.120,
		bottomDiameter: 0.194,
		baseHeight: 1.2,
	},
	wood__conical__7: {
		height: 7,
		topMargin: topMargin,
		topDiameter: 0.140,
		bottomDiameter: 0.220,
		baseHeight: 1.35,
	},
	wood__conical__8: {
		height: 8,
		topMargin: topMargin,
		topDiameter: 0.140,
		bottomDiameter: 0.220,
		baseHeight: 1.35,
	},
	wood__conical__9: {
		height: 9,
		topMargin: topMargin,
		topDiameter: 0.140,
		bottomDiameter: 0.220,
		baseHeight: 1.35,
	},
	wood__conical__10: {
		height: 10,
		topMargin: topMargin,
		topDiameter: 0.140,
		bottomDiameter: 0.245,
		baseHeight: 1.5,
	},
	wood__conical__11: {
		height: 11,
		topMargin: topMargin,
		topDiameter: 0.140,
		bottomDiameter: 0.245,
		baseHeight: 1.5,
	},
	wood__conical__12: {
		height: 12,
		topMargin: topMargin,
		topDiameter: 0.140,
		bottomDiameter: 0.275,
		baseHeight: 1.5,
	},

	wood__cylindrical__3__140: {
		height: 3,
		topMargin: topMargin,
		topDiameter: 0.140,
		bottomDiameter: 0.140,
		verticalAngle: 0,
		maxHeight: 3 - topMargin,
		ratio: 0,
		baseHeight: 1.1,
	},
	wood__cylindrical__3__168: {
		height: 3,
		topMargin: topMargin,
		topDiameter: 0.168,
		bottomDiameter: 0.168,
		verticalAngle: 0,
		maxHeight: 3 - topMargin,
		ratio: 0,
		baseHeight: 1.1,
	},
	wood__cylindrical__3__220: {
		height: 3,
		topMargin: topMargin,
		topDiameter: 0.220,
		bottomDiameter: 0.220,
		verticalAngle: 0,
		maxHeight: 3 - topMargin,
		ratio: 0,
		baseHeight: 1.1,
	},
	wood__cylindrical__4__140: {
		height: 4,
		topMargin: topMargin,
		topDiameter: 0.140,
		bottomDiameter: 0.140,
		verticalAngle: 0,
		maxHeight: 4 - topMargin,
		ratio: 0,
		baseHeight: 1.1,
	},
	wood__cylindrical__4__168: {
		height: 4,
		topMargin: topMargin,
		topDiameter: 0.168,
		bottomDiameter: 0.168,
		verticalAngle: 0,
		maxHeight: 4 - topMargin,
		ratio: 0,
		baseHeight: 1.1,
	},
	wood__cylindrical__4__220: {
		height: 4,
		topMargin: topMargin,
		topDiameter: 0.220,
		bottomDiameter: 0.220,
		verticalAngle: 0,
		maxHeight: 4 - topMargin,
		ratio: 0,
		baseHeight: 1.1,
	},
	wood__cylindrical__5__140: {
		height: 5,
		topMargin: topMargin,
		topDiameter: 0.140,
		bottomDiameter: 0.140,
		verticalAngle: 0,
		maxHeight: 5 - topMargin,
		ratio: 0,
		baseHeight: 1.1,
	},
	wood__cylindrical__5__168: {
		height: 5,
		topMargin: topMargin,
		topDiameter: 0.168,
		bottomDiameter: 0.168,
		verticalAngle: 0,
		maxHeight: 5 - topMargin,
		ratio: 0,
		baseHeight: 1.2,
	},
	wood__cylindrical__5__220: {
		height: 5,
		topMargin: topMargin,
		topDiameter: 0.220,
		bottomDiameter: 0.220,
		verticalAngle: 0,
		maxHeight: 5 - topMargin,
		ratio: 0,
		baseHeight: 1.35,
	},
	wood__cylindrical__6__168: {
		height: 6,
		topMargin: topMargin,
		topDiameter: 0.168,
		bottomDiameter: 0.168,
		verticalAngle: 0,
		maxHeight: 6 - topMargin,
		ratio: 0,
		baseHeight: 1.2,
	},
	wood__cylindrical__6__220: {
		height: 6,
		topMargin: topMargin,
		topDiameter: 0.220,
		bottomDiameter: 0.220,
		verticalAngle: 0,
		maxHeight: 6 - topMargin,
		ratio: 0,
		baseHeight: 1.35,
	},
	wood__cylindrical__7__220: {
		height: 7,
		topMargin: topMargin,
		topDiameter: 0.220,
		bottomDiameter: 0.220,
		verticalAngle: 0,
		maxHeight: 7 - topMargin,
		ratio: 0,
		baseHeight: 1.35,
	},
};

const sep = OlivioLuminaire.conf.keySep;
for (const size of ['piccolo', 'medio-grande']) {
	for (let h = 5; h <= 12; h++) {
		const id = [conf.metalMaterial, size, h].join(sep);
		const diameter = size === 'piccolo' ? 0.140 : 0.219;
		poleSizes[id] = {
			height: h,
			topMargin: topMargin,
			topDiameter: diameter,
			bottomDiameter: diameter,
			verticalAngle: 0,
			maxHeight: h - topMargin,
		};
	}
}

for (const id of Object.keys(poleSizes)) {
	if (id.startsWith('wood__conical')) {
		const entry = poleSizes[id];
		const h = entry.height - entry.baseHeight;
		const b = (entry.bottomDiameter / 2) - (entry.topDiameter / 2);
		entry.ratio = b / h;
		entry.maxHeight = entry.height - entry.topMargin;
		const i = Math.sqrt(h**2 + b**2);
		entry.verticalAngle = Math.asin(b / i) * (180 / Math.PI); // deg
	}
}


const woodMaterialsMap = {
	brume: 'canadian-brown',
	tonnelier: 'redwood',
	'chêne-de-ferrare': 'cool-scandinavian',
	// 'chêne-de-ferrare': 'canadian-brown',
	aubrilam: 'rocky-mountain-grey',
	'light mineral': 'himalaya-grey',
	'dark mineral': 'teneriffa-grey'
};


class OlivioFloracionLuminaire extends OlivioLuminaire {
	static designName = 'floracion';

	static conf = conf;

	static poleSizes = poleSizes;

	static poleConstraints = {
		steel: {
			poleHeight: [5, 6, 7, 8, 9, 10, 11, 12],
		},
		wood_conical: {
			poleHeight: [5, 6, 7, 8, 9, 10, 11, 12],
		},
		wood_cylindrical: {
			poleHeight: [3, 4, 5, 6, 7],
			poleDiameter: {
				3: [140, 168, 220],
				4: [140, 168, 220],
				5: [140, 168, 220],
				6: [168, 220],
				7: [220],
			},
		}
	};

	static woodMaterials = {};

	static models = {
		pole: {},
		bracket: {},
		cutout: {},
	};

	static modelPatterns = [
		{type: 'pole', pattern: /^Floracion_Pole_(?<size>(Medio-Grande)|(Piccolo))_+(?<id>[0-9]+)$/i, storageKeys: [
			{key: 'materialType', value: conf.metalMaterial},
			'size',
			'id'
		]},
		{type: 'pole', pattern: /^Floracion_Pole_Wood_Conical_(?<id>[0-9]+)_[0-9]+$/i, storageKeys: [
			{key: 'materialType', value: conf.woodMaterial},
			{key: 'shape', value: 'conical'},
			'id'
		]},
		{type: 'pole', pattern: /^Floracion_Pole_Wood_Cylindrical_(?<id>[0-9]+)_(?<diameter>[0-9]+)$/i, storageKeys: [
			{key: 'materialType', value: conf.woodMaterial},
			{key: 'shape', value: 'cylindrical'},
			'id',
			'diameter'
		]},
		{type: 'bracket', pattern: /^Floracion_Inlay_(?<id>(Medio-Grande)|(Piccolo))$/i, storageKeys: [
			{key: 'materialType', value: conf.metalMaterial},
			'id'
		]},
		{type: 'bracket', pattern: /^Floracion_Inlay_Wood_(?<id>(Medio-Grande)|(Piccolo))$/i, storageKeys: [
			{key: 'materialType', value: conf.woodMaterial},
			'id'
		]},
		{type: 'cutout', pattern: /^Floracion_Cutout_(?<id>(Medio-Grande)|(Piccolo))$/i, storageKeys: [
			{key: 'materialType', value: conf.metalMaterial},
			'id'
		]},
		{type: 'cutout', pattern: /^Floracion_Cutout_Wood_(?<id>(Medio-Grande)|(Piccolo))$/i, storageKeys: [
			{key: 'materialType', value: conf.woodMaterial},
			'id'
		]},
	];

	static headLinkOffsets = {};
	static internalOffsets = null;

	static requireCutouts = true;

	static defaultConfig = {
		poleHeight: 12,
		numberOfHeads: 8,
		layout: 'spiral',
		heightMax: 11.20,
		heightMin: 3.00,
		poleMaterial: 'steel',
		poleDiameter: 0.219,
		angleMax: 360,
		angleMin: 0,
		bracket: 'sistema1',
	};


	evaluator = null;


	constructor(configValues = null) {
		super(configValues);
		if (configValues !== null) {
			this.updatePoleCutouts();
		}
	}

	static getBracketHorizontalOffset(configValues, height, index) {
		// newB / newH = b / h;
		const entry = this.poleSizes[this.getPoleId(configValues)];
		const [materialType, shape] = configValues.poleMaterial.split('_');
		const housingSize = String(configValues.heads[index].housingSize ? configValues.heads[index].housingSize : configValues.housingSize).toLowerCase();
		const size = housingSize.includes('piccolo') ? 'piccolo' : 'medio-grande';
		const isWood = materialType === conf.woodMaterial;
		if (isWood) {
			const newH = entry.height - height;
			const newDiameter = entry.ratio * newH * 2 + entry.topDiameter;
			return getWoodHorizontalMarginByDiameter(size, newDiameter);
		} else {
			return 0;
		}
	}


	static processPoleModel(model, entry, matches) {
		// console.log('POLE', model, model.material.name);
		if (model.userData.materialType === conf.woodMaterial && model.material) {
			if (model.material.name in woodMaterialsMap) {
				const material = model.material;
				const materialKey = woodMaterialsMap[material.name];
				if (!(materialKey in this.woodMaterials)) {
					this.woodMaterials[materialKey] = material;
				}
			} else {
				console.log('MISSING WOOD MAPPING', model.material.name, woodMaterialsMap);
			}
		}
		return model;
	}

	static getPoleModel(configValues) {
		const pole = super.getPoleModel(configValues);
		if (pole.userData.materialType === conf.woodMaterial) {
			const woodType = configValues.woodType.toLowerCase();
			if (woodType in this.woodMaterials) {
				pole.material = this.woodMaterials[woodType];
				// pole.material.transparent = true;
				// pole.material.opacity = 0.1;
			} else {
				console.log('NO WOOD FOUND', woodType, this.woodMaterials);
			}
		}
		return pole;
	}

	static getPoleId(configValues) {
		const [materialType, shape] = configValues.poleMaterial.split('_');
		let idParts = null;
		const height = String(configValues.poleHeight);
		if (materialType === conf.metalMaterial) {
			const size = String(configValues.housingSize).toLowerCase().includes('piccolo') ? 'piccolo' : 'medio-grande';
			idParts = [materialType, size, height];
		} else {
			const id = String(configValues.poleHeight);
			idParts = [materialType, shape, height];
			if (shape === 'cylindrical') {
				idParts.push(configValues.poleDiameter);
			}
		}

		return idParts.join(this.conf.keySep);
	}

	static getBracketId(configValues, index) {
		const [materialType, shape] = configValues.poleMaterial.split('_');
		const housingSize = String(configValues.heads[index].housingSize ? configValues.heads[index].housingSize : configValues.housingSize).toLowerCase();
		const size = housingSize.includes('piccolo') ? 'piccolo' : 'medio-grande';
		return [materialType, size].join(conf.keySep);
	}


	static calculateLaValue(poleHeight, actualHeight) {
		return actualHeight - 0.212; // TODO non magick offset
	}

	static getCutoutModel(configValues, index) {
		const [materialType, shape] = configValues.poleMaterial.split('_');
		const housingSize = String(configValues.heads[index].housingSize ? configValues.heads[index].housingSize : configValues.housingSize).toLowerCase();
		const size = housingSize.toLowerCase().includes('piccolo') ? 'piccolo' : 'medio-grande';
		const id = [materialType, size].join(conf.keySep);
		return this.models.cutout[id];
	}

	static getPoleColor(configValues) {
		const [materialType, shape] = configValues.poleMaterial.split('_');
		if (materialType === conf.woodMaterial) {
			return this.getHeadColor(configValues);
		}
		return configValues.colorPole;
	}

	static getBracketColor(configValues) {
		const [materialType, shape] = configValues.poleMaterial.split('_');
		if (materialType === conf.woodMaterial) {
			return this.getHeadColor(configValues);
		}
		return configValues.colorInlay;
	}

	static getColorForLightTuning(configValues) {
		const [materialType, shape] = configValues.poleMaterial.split('_');
		if (materialType === conf.woodMaterial) {
			return '#909090';
		}
		return super.getColorForLightTuning(configValues);
	}

	update(configValues) {
		const materialChanged = this.isFeatureChanged(configValues, 'woodType');
		const flags = super.update(configValues);
		const isWood = this.pole.userData.materialType === conf.woodMaterial;
		if (isWood && materialChanged) {
			this.setPoleWoodMaterial(configValues.woodType);
		}
		return flags;
	}


	setPoleWoodMaterial(woodType) {
		const woodMaterials = this.constructor.woodMaterials;
		woodType = woodType.toLowerCase();
		if (woodType in woodMaterials) {
			const material = woodMaterials[woodType];
			if (Array.isArray(this.pole.material)) {
				for (let i = 0; i < this.pole.material.length; i++) {
					this.pole.material[i] = material;
				}
			} else {
				this.pole.material = material;
			}
		}
	}


	getBracketCollisionBoxInfo(configValues, bracket, bracketMesh) {
		const self = this.constructor;
		let bracketOffset = null;
		let bracketSize = null;
		let bracketBox = new Box3();
		bracketBox.setFromObject(bracket);
		bracketSize = new Vector3();
		const [materialType, shape] = configValues.poleMaterial.split('_');
		if (materialType === conf.woodMaterial) {
			bracketBox = new Box3(
				new Vector3(bracketBox.min.x, bracketBox.min.y, bracketBox.min.z - 0.02),
				new Vector3(bracketBox.max.x - 0.04, bracketBox.max.y + 0.12, bracketBox.max.z + 0.02)
			);
		}
		bracketBox.getSize(bracketSize);
		bracketOffset = new Vector3();
		bracketBox.getCenter(bracketOffset);
		bracketOffset.x -= bracketMesh.position.x;
		return {bracketOffset: bracketOffset, bracketSize: bracketSize};
	}


	updatePoleCutouts() {
		let poleChanged = false;
		if (!('heads' in this.configValues)) {
			return false;
		}
		const values = [];
		const offsets = [];
		const heights = [];
		const eulers = [];
		for (let i = 0; i < this.configValues.heads.length; i++) {
			const head = this.configValues.heads[i];
			const branch = this.branches[i];
			if (head.active) {
				const offset = branch.bracketMesh.position.x;
				const height = branch.bracket.position.y;
				const euler = branch.bracket.rotation;
				values.push(offset.toPrecision(3));
				values.push(height.toPrecision(3));
				values.push([euler.x.toPrecision(3), euler.y.toPrecision(3), euler.z.toPrecision(3), euler.order].join('-'));
				offsets[i] = offset;
				heights[i] = height;
				eulers[i] = euler;
			} else {
				offsets[i] = null;
				heights[i] = null;
				eulers[i] = null;
			}
		}
		const configHash = values.join(',');
		if (!('configHash' in this.pole.userData) || this.pole.userData.configHash !== configHash) {
			poleChanged = true;
			const self = this.constructor;
			// console.time('subtracts');
			this.pole.updateMatrixWorld();
			if (this.pole.parent) {
				this.pole.parent.remove(this.pole);
			}
			const quaternion = this.pole.quaternion.clone();
			const children = [...this.pole.children];
			for (const child of children) {
				this.pole.remove(child);
			}

			const model = self.getPoleModel(this.configValues);
			const poleMaterial = model.material;
			const isWood = model.userData.materialType === conf.woodMaterial;
			const getMaterial = () => (isWood ? poleMaterial : self.getRealisticMaterial(this.configValues.staticView));

			if (!this.evaluator) {
				this.evaluator = new Evaluator();
				// this.evaluator.useGroups = true; //params.useGroups;
			}

			let finalPole = new Brush();
			let base = new Brush(model.geometry.clone(true), getMaterial());
			base.updateMatrixWorld();

			for (let i = 0; i < heights.length; i++) {
				if (heights[i] === null) {
					continue;
				}
				const holeModel = self.getCutoutModel(this.configValues, i);
				const hole = new Brush(
					holeModel.geometry.clone(true),
					getMaterial()
				);
				hole.position.set(0, 0, 0);
				hole.rotation.set(0, 0, 0);
				const holeWrapper = new Object3D();
				holeWrapper.add(hole);
				holeWrapper.position.y = heights[i];
				holeWrapper.rotation.copy(eulers[i]);
				hole.position.x = offsets[i];
				hole.updateMatrixWorld();
				const worldPos = new Vector3();
				const worldRot = new Quaternion();
				hole.getWorldPosition(worldPos);
				hole.rotation.copy(eulers[i]); //(worldRot);
				holeWrapper.remove(hole);
				hole.position.copy(worldPos);
				hole.updateMatrixWorld();
				finalPole = this.evaluator.evaluate(base, hole, SUBTRACTION, finalPole);
				finalPole.geometry = finalPole.geometry.clone(true);
				base = finalPole;
				// break;
			}
			this.pole = finalPole;
			this.pole.castShadow = true;
			this.pole.receiveShadow = true;
			this.pole.userData.configHash = configHash;
			this.pole.name = model.name;
			this.setColor(this.pole, self.getPoleColor(this.configValues), false);

			for (const child of children) {
				this.pole.add(child);
			}

			this.pole.updateMatrixWorld();
			this.pole.quaternion.copy(quaternion);
			Object.assign(this.pole.userData, model.userData);
			// console.timeEnd('subtracts');
			// console.log('final pole', finalPole);
		}
		return poleChanged;
	}

}

export {OlivioFloracionLuminaire};

